import { useEffect, useCallback } from 'react';

/**
 * Custom hook for common modal behavior (escape key and overlay click handling)
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal should close
 * @param {object} options - Configuration options
 * @param {boolean} options.closeOnEscape - Whether to close on escape key (default: true)
 * @param {boolean} options.closeOnOverlayClick - Whether to close on overlay click (default: true)
 * @returns {object} - { handleOverlayClick } handler for overlay click
 */
export default function useModalBehavior(isOpen, onClose, options = {}) {
  const { closeOnEscape = true, closeOnOverlayClick = true } = options;

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, closeOnEscape]);

  // Handle overlay click
  const handleOverlayClick = useCallback((e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget && onClose) {
      onClose();
    }
  }, [onClose, closeOnOverlayClick]);

  return { handleOverlayClick };
}

